import React, { useState } from 'react';
import { Mail, Lock, LogIn, AlertCircle, Clock3, Database, ShieldCheck, Moon, Sun } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '@/auth/AuthContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EMAIL_INPUT_PATTERN, isValidEmail } from '@/utils/formValidation';
import { useTheme } from '@/theme/ThemeContext';

// Mock do Spinner para manter o arquivo limpo
const Spinner = () => (
    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
);

export default function LoginForm() {
    const { login } = useAuth();
    const { isDark, toggleTheme } = useTheme();
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sessionExpired = searchParams.get('session') === 'expired';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!isValidEmail(email)) {
            setError('Informe um e-mail válido, como usuario@dominio.com.br.');
            return;
        }

        setLoading(true);

        try {
            // 1. Captura a resposta do login
            const userData = await login(email, password);
            // 2. Executa a regra de negócio das secretarias (Regra de Ouro)
            if (userData && userData.secretaries) {
                if (userData.secretaries.length === 1) {
                    // O contexto de autenticação já seleciona a única secretaria disponível.
                    navigate(userData?.role==='VIEWER'?'/reports':'/dashboard');
                } else if (userData.secretaries.length > 1) {
                    // Se tiver mais de uma, envia para a tela de seleção de cards
                    navigate('/select-secretary');
                } else {
                    // Se o usuário não tiver nenhuma secretaria vinculada no banco
                    setError('Seu usuário não possui nenhuma Secretaria vinculada. Contate o Administrador.');
                }
            } else {
                // Fallback caso a API mude o formato da resposta
                navigate(userData?.role==='VIEWER'?'/reports':'/dashboard');
            }
        } catch (err: unknown) {
            if (axios.isAxiosError(err)) {
                const message = err.response?.data?.message || 'Erro ao conectar com o servidor.';
                setError(message);
            } else {
                setError('Credenciais inválidas ou erro no servidor.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-shell">
            <motion.button
                type="button"
                onClick={toggleTheme}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.96 }}
                className="theme-auth-toggle absolute right-4 top-4 z-20 flex items-center gap-2 rounded-2xl border border-white/5 bg-white/5 px-3.5 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-400 shadow-lg backdrop-blur-xl transition-colors hover:border-blue-500/20 hover:bg-blue-500/10 hover:text-blue-400 sm:right-6 sm:top-6"
                title={isDark ? 'Ativar tema claro' : 'Ativar tema escuro'}
                aria-label={isDark ? 'Ativar tema claro' : 'Ativar tema escuro'}
            >
                {isDark ? <Sun size={15} /> : <Moon size={15} />}
                <span className="hidden sm:inline">{isDark ? 'Tema claro' : 'Tema escuro'}</span>
            </motion.button>

            {/* Elementos Decorativos */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/10 blur-[120px] rounded-full" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[30%] h-[30%] bg-blue-500/5 blur-[100px] rounded-full" />
            </div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="relative z-10 w-full max-w-[440px]"
            >
                <div className="flex flex-col items-center mb-10 text-center">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-blue-600 rounded-2xl shadow-2xl shadow-blue-600/30 text-white transform -rotate-3">
                            <Database size={32} />
                        </div>
                        <h1 className="app-page-title text-4xl font-black tracking-tighter italic">
                            IT<span className="text-blue-500">MANAGER</span>
                        </h1>
                    </div>
                    <h2 className="text-xl font-bold text-slate-100 tracking-tight">Acesso ao Sistema</h2>
                    <p className="text-slate-500 text-sm mt-1 font-medium">Gestão para Tecnologia da Informação</p>
                </div>

                <div className="auth-card max-w-none">
                    {sessionExpired && !error && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-8 flex items-center gap-3 rounded-2xl border border-amber-400/20 bg-amber-400/10 p-4 text-xs font-bold uppercase tracking-wider text-amber-300"
                        >
                            <Clock3 size={18} className="shrink-0" />
                            <span>Sua sessão expirou. Entre novamente para continuar.</span>
                        </motion.div>
                    )}
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold p-4 rounded-2xl flex items-center gap-3 mb-8 uppercase tracking-wider"
                        >
                            <AlertCircle size={18} className="shrink-0" />
                            <span>{error}</span>
                        </motion.div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-500 uppercase ml-1 tracking-[2px]">E-mail</label>
                            <div className="relative group">
                                <span className="absolute inset-y-0 left-5 flex items-center text-slate-600 group-focus-within:text-blue-500 transition-colors">
                                    <Mail size={18} />
                                </span>
                                <input
                                    type="email"
                                    pattern={EMAIL_INPUT_PATTERN}
                                    title="Informe um e-mail válido, como usuario@dominio.com.br."
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="auth-input pl-14 pr-6"
                                    placeholder="exemplo@coreassets.com"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex justify-between items-center ml-1">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[2px]">Senha de Acesso</label>
                            </div>
                            <div className="relative group">
                                <span className="absolute inset-y-0 left-5 flex items-center text-slate-600 group-focus-within:text-blue-500 transition-colors">
                                    <Lock size={18} />
                                </span>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="auth-input pl-14 pr-6"
                                    placeholder="••••••••"
                                    required
                                />
                            </div>
                            <Link to="/forgot-password" className="block text-right text-xs font-semibold text-blue-600 hover:underline">Esqueceu a senha?</Link>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="group w-full bg-blue-600 hover:bg-blue-500 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-[3px] shadow-xl shadow-blue-600/20 transition-all active:scale-[0.98] flex items-center justify-center gap-3 disabled:bg-slate-800 disabled:text-slate-600 disabled:cursor-not-allowed mt-4"
                        >
                            {loading ? <Spinner /> : (
                                <>
                                    <span>Iniciar Sessão</span>
                                    <LogIn size={16} className="group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-10 pt-8 border-t border-white/5">
                        <div className="flex items-center justify-center gap-2 text-slate-600 font-bold text-[10px] uppercase tracking-widest">
                            <ShieldCheck size={14} className="text-emerald-500" />
                            Sessão protegida por HTTPS e cookies seguros
                        </div>
                    </div>
                </div>

                <footer className="mt-8 text-center space-y-4">
                    <p className="text-[10px] text-slate-600 font-bold uppercase tracking-[4px]">
                        © {new Date().getFullYear()} PortoSoftware • v2.4.0
                    </p>
                </footer>
            </motion.div>
        </div>
    );
}
